import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';

export default function CommonDeleteModal({
  open,
  onClose,
  onConfirm,
  isDeleting,
  entityName, 
  entityType = 'Item',
  isBulk = false,
  bulkCount = 0,
}) {
  return (
    <Dialog
      open={open}
      onClose={isDeleting ? undefined : onClose}
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          bgcolor: 'var(--canvas, #FFF)',
          boxShadow: '0 8px 32px rgba(11, 37, 69, 0.15)',
          border: 'none',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          width: '750px',
          maxWidth: '100%'
        }
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(11, 37, 69, 0.5)',
        }
      }}
    >
      <Box 
        sx={{ 
          width: { xs: '100%', md: '35%' }, 
          display: { xs: 'none', md: 'flex' },
          bgcolor: '#FFF5F6', 
          p: 4, 
          flexDirection: 'column', 
          alignItems: 'flex-start', 
          justifyContent: 'center',
          borderRight: '1px solid #FFE5E6' 
        }}
      >
        <Box 
          sx={{ 
            width: 64, 
            height: 64, 
            borderRadius: '50%', 
            bgcolor: '#FFEAEB', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            mb: 4 
          }}
        >
          <DeleteOutlineIcon sx={{ color: '#D32F2F', fontSize: 36 }} />
        </Box>
        <Typography 
          sx={{ 
            fontFamily: 'Arial, sans-serif', 
            fontWeight: 'bold', 
            fontSize: '24px', 
            color: 'var(--vault-navy, #0B2545)', 
            mb: 2 
          }}
        >
          Delete {isBulk ? `${bulkCount} ${entityType}s` : entityType}
        </Typography>
        <Typography 
          sx={{ 
            fontFamily: 'Calibri, sans-serif', 
            fontSize: '16px', 
            color: 'var(--slate, #64748B)', 
            lineHeight: 1.5 
          }}
        >
          This will permanently remove the {isBulk ? 'selected items' : entityType.toLowerCase()} and all its associations.
        </Typography>
      </Box>
      <Box 
        sx={{ 
          width: { xs: '100%', md: '65%' }, 
          bgcolor: 'var(--canvas, #FFF)', 
          p: { xs: 3, md: 4 }, 
          pt: { xs: 6, md: 4 }, // Extra padding on mobile so text doesn't hit close button
          position: 'relative', 
          display: 'flex', 
          flexDirection: 'column' 
        }}
      >
        <IconButton 
          onClick={onClose} 
          disabled={isDeleting}
          sx={{ position: 'absolute', top: 16, right: 16, color: 'var(--slate, #64748B)' }}
        >
          <CloseIcon />
        </IconButton>
        
        <Typography 
          sx={{ 
            fontFamily: 'Arial, sans-serif', 
            fontWeight: 'bold', 
            fontSize: '22px', 
            color: 'var(--vault-navy, #0B2545)', 
            mt: 2, 
            mb: 2,
            lineHeight: 1.4
          }}
        >
          Are you sure you want to delete {isBulk ? 'these' : 'the'} {entityType.toLowerCase()}{' '}
          <Box component="span" sx={{ color: '#D32F2F' }}>
            {isBulk ? `${bulkCount} selected` : entityName}
          </Box>?
        </Typography>
        
        <Typography 
          sx={{ 
            fontFamily: 'Calibri, sans-serif', 
            fontSize: '16px', 
            color: 'var(--slate, #64748B)', 
            mb: 4, 
            lineHeight: 1.5 
          }}
        >
          This action cannot be undone. All associated data will be permanently removed.
        </Typography>

        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            bgcolor: '#FFF5F6', 
            p: 2, 
            borderRadius: '8px', 
            mb: 4 
          }}
        >
          <WarningAmberIcon sx={{ color: '#D32F2F' }} />
          <Typography 
            sx={{ 
              fontFamily: 'Calibri, sans-serif', 
              fontSize: '15px', 
              color: '#D32F2F', 
              fontWeight: 'bold' 
            }}
          >
            This action is permanent and cannot be undone.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 'auto', borderTop: '1px solid var(--hairline)', pt: 3 }}>
          <Button
            onClick={onClose}
            disabled={isDeleting}
            sx={{
              color: 'var(--vault-navy, #0B2545)',
              fontFamily: 'Arial, sans-serif',
              fontWeight: 'bold',
              fontSize: '15px',
              textTransform: 'none',
              border: '1px solid var(--hairline)',
              borderRadius: '8px',
              px: 3,
              py: 1,
              '&:hover': { bgcolor: 'var(--mist, #F8FAFC)' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isDeleting}
            startIcon={<DeleteOutlineIcon />}
            sx={{
              bgcolor: '#D32F2F',
              color: '#FFF',
              fontFamily: 'Arial, sans-serif',
              fontWeight: 'bold',
              fontSize: '15px',
              textTransform: 'none',
              borderRadius: '8px',
              px: 3,
              py: 1,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#B71C1C', boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: '#FFCDD2', color: '#FFF' }
            }}
          >
            {isDeleting ? 'Deleting...' : 'Yes, Delete'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
